import React, { useState, useEffect } from "react";
import {
  ReferenceValue,
  ReferenceList,
  ReferenceValueSynonymView
} from "@/entities/all";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  PlusCircle,
  Users,
  Trash2,
  Pencil
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import { toast } from "sonner";

import EditValueModal from "../components/references/EditValueModal";
import DeleteValueDialog from "../components/references/DeleteValueDialog";
import CreateSynonymModal from "../components/references/CreateSynonymModal";
import EditSynonymModal from "../components/references/EditSynonymModal";
import DeleteSynonymDialog from "../components/references/DeleteSynonymDialog";

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-page-muted">{label}</span>
      <div className="text-sm font-medium text-page-text">{children}</div>
    </div>
  );
}

function SectionCard({ icon: Icon, title, count, children }) {
  return (
    <div className="bg-card-bg rounded-[8px] overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3 border-b border-card-border">
        {Icon && <Icon size={18} className="text-nav-active" />}
        <h3 className="text-sm font-semibold text-page-text">{title}</h3>
        {typeof count === 'number' && (
          <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 bg-nav-active/10 text-xs font-medium text-nav-active ml-1">
            {count}
          </span>
        )}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function ReferenceValueDetails() {
  const { listId, valueId } = useParams();
  const navigate = useNavigate();

  const [referenceList, setReferenceList] = useState(null);
  const [value, setValue] = useState(null);
  const [synonyms, setSynonyms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreateSynonymModalOpen, setIsCreateSynonymModalOpen] = useState(false);
  const [isEditSynonymModalOpen, setIsEditSynonymModalOpen] = useState(false);
  const [isDeleteSynonymDialogOpen, setIsDeleteSynonymDialogOpen] = useState(false);
  const [selectedSynonym, setSelectedSynonym] = useState(null);

  useEffect(() => {
    if (listId && valueId) {
      loadData();
    }
  }, [listId, valueId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [listData, valueData, synonymsData] = await Promise.all([
        ReferenceList.filter({ id: listId }).then(lists => lists[0]),
        ReferenceValue.filter({ id: valueId }).then(values => values[0]),
        ReferenceValueSynonymView.filter({ reference_value_id: valueId })
      ]);
      setReferenceList(listData);
      setValue(valueData);
      setSynonyms(synonymsData);
    } catch (error) {
      console.error('Error loading reference value data:', error);
      toast.error('Failed to load reference value');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      await ReferenceValue.update(valueId, { is_active: !value.is_active });
      setValue(prev => ({ ...prev, is_active: !prev.is_active }));
      toast.success('Value status updated');
    } catch (error) {
      console.error('Error updating value status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleEditSynonym = (synonym) => {
    setSelectedSynonym(synonym);
    setIsEditSynonymModalOpen(true);
  };

  const handleDeleteSynonym = (synonym) => {
    setSelectedSynonym(synonym);
    setIsDeleteSynonymDialogOpen(true);
  };

  const handleBack = () => {
    navigate(createPageUrl('reference-list-values') + `?id=${listId}`);
  };

  if (isLoading) {
    return (
      <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-[180px] w-full rounded-[8px]" />
        <Skeleton className="h-[300px] w-full rounded-[8px]" />
      </div>
    );
  }

  if (!value) {
    return (
      <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
        <div className="bg-card-bg rounded-[8px] flex flex-col items-center justify-center py-16 gap-3">
          <p className="text-sm text-page-muted">Reference value not found</p>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Reference List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Back */}
      <button
        onClick={handleBack}
        className="flex items-center gap-1 w-fit text-sm text-nav-active cursor-pointer bg-transparent border-0 p-0 hover:text-nav-active-hover transition-colors"
      >
        <ArrowLeft size={16} />
        Back to {referenceList?.list_name || 'List'}
      </button>

      {/* Title + Actions */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">
            {value.canonical_value}
          </h1>
          <p className="text-sm font-normal text-page-muted">
            {value.value_description || 'No description'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <button
            onClick={() => setIsDeleteDialogOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 text-sm font-medium cursor-pointer bg-card-bg hover:bg-red-500/10 transition-colors"
            style={{ borderColor: 'rgba(222,111,107,0.7)', color: 'rgba(222,111,107,0.9)' }}
          >
            <Trash2 size={20} strokeWidth={2.5} />
            Delete
          </button>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <Pencil size={20} strokeWidth={2.5} />
            Edit
          </button>
          <button
            onClick={() => setIsCreateSynonymModalOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <PlusCircle size={24} />
            Add Synonym
          </button>
        </div>
      </div>

      {/* Status switch */}
      <div className="flex items-center gap-2">
        <Switch checked={value.is_active} onCheckedChange={handleToggleActive} />
        <span className="text-sm text-page-text">
          {value.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Value Details */}
      <SectionCard icon={Users} title="Value Details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
          <Field label="Reference List">{referenceList?.list_name || '—'}</Field>
          <Field label="Created">
            {value.created_date ? format(new Date(value.created_date), 'MMM d, yyyy') : '—'}
          </Field>
        </div>
      </SectionCard>

      {/* Synonyms */}
      <SectionCard icon={Users} title="Synonyms" count={synonyms.length}>
        {synonyms.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <Users size={32} className="text-page-muted" />
            <p className="text-sm text-page-muted">No synonyms for this value</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-5">
            <div className="flex flex-col min-w-[700px]">
              {/* Header */}
              <div className="flex items-center h-9 px-5 gap-[10px] bg-table-header">
                <div className="flex-1 min-w-0 text-sm font-medium text-nav-active">Synonym</div>
                <div className="w-[140px] shrink-0 text-sm font-medium text-nav-active">Status</div>
                <div className="w-[140px] shrink-0 text-sm font-medium text-nav-active">Created</div>
                <div className="w-[80px] shrink-0" />
              </div>
              {/* Rows */}
              {synonyms.map((synonym, i) => (
                <div
                  key={synonym.id}
                  className={`group flex items-center h-11 px-5 gap-[10px] ${i % 2 === 0 ? 'bg-table-stripe' : 'bg-card-bg'}`}
                >
                  <div className="flex-1 min-w-0 text-sm font-medium truncate text-page-text">
                    {synonym.synonym}
                  </div>
                  <div className="w-[140px] shrink-0">
                    <span
                      className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1 pr-2 text-xs font-medium"
                      style={
                        synonym.is_approved
                          ? { background: 'var(--pill-green-bg)', color: 'var(--pill-green-color)' }
                          : { background: 'var(--pill-amber-bg)', color: 'var(--pill-amber-color)' }
                      }
                    >
                      <span
                        className="size-[10px] rounded-full shrink-0"
                        style={{
                          background: synonym.is_approved
                            ? 'var(--pill-green-color)'
                            : 'var(--pill-amber-color)',
                        }}
                      />
                      {synonym.is_approved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                  <div className="w-[140px] shrink-0 text-sm text-page-text">
                    {synonym.created_date ? format(new Date(synonym.created_date), 'MMM d, yyyy') : '—'}
                  </div>
                  <div className="w-[80px] shrink-0 flex items-center justify-end gap-1">
                    <button
                      onClick={() => handleEditSynonym(synonym)}
                      className="flex items-center justify-center w-7 h-7 rounded cursor-pointer border-0 bg-transparent text-nav-hover hover:text-nav-active transition-colors"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteSynonym(synonym)}
                      className="flex items-center justify-center w-7 h-7 rounded cursor-pointer border-0 bg-transparent text-nav-hover hover:text-nav-active transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {/* Modals */}
      <EditValueModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onValueUpdated={() => {
          setIsEditModalOpen(false);
          loadData();
        }}
        value={value}
      />

      <DeleteValueDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onValueDeleted={() => navigate(createPageUrl('reference-list-values') + `?id=${listId}`)}
        value={value}
      />

      <CreateSynonymModal
        isOpen={isCreateSynonymModalOpen}
        onClose={() => setIsCreateSynonymModalOpen(false)}
        onSynonymCreated={() => {
          setIsCreateSynonymModalOpen(false);
          loadData();
        }}
        values={[value]}
        selectedValueId={value?.id}
      />

      {selectedSynonym && (
        <>
          <EditSynonymModal
            isOpen={isEditSynonymModalOpen}
            onClose={() => { setIsEditSynonymModalOpen(false); setSelectedSynonym(null); }}
            onSynonymUpdated={() => {
              setIsEditSynonymModalOpen(false);
              setSelectedSynonym(null);
              loadData();
            }}
            synonym={selectedSynonym}
          />

          <DeleteSynonymDialog
            isOpen={isDeleteSynonymDialogOpen}
            onClose={() => { setIsDeleteSynonymDialogOpen(false); setSelectedSynonym(null); }}
            onSynonymDeleted={() => {
              setIsDeleteSynonymDialogOpen(false);
              setSelectedSynonym(null);
              loadData();
            }}
            synonym={selectedSynonym}
          />
        </>
      )}
    </div>
  );
}
